/**
 * 本地兜底怪兽生成（不依赖网络）
 *
 * 当 /api/generate 失败时，main.ts 调 generateLocalMonster() 拿占位怪兽
 * 占位怪兽继承分析得到的 element + scpId 稳定哈希
 */

import type { CharAnalysis, MonsterDesc, ThreatLevel } from "../shared/types";

/** 简易威胁等级（根据 fortune.total） */
function threatFromFortune(total: number): ThreatLevel {
  if (total >= 60) return "Keter";
  if (total >= 30) return "Euclid";
  return "Safe";
}

/** 用名字生成稳定 scpId */
function scpIdFor(name: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const num = (h >>> 0) % 9999;
  return `SCP-${String(num).padStart(4, "0")}`;
}

/** 用 fortune + dominant 推 class 名（占位） */
function classFor(element: string, total: number): string {
  if (total >= 70) return `${element}系 · 太和`;
  if (total >= 50) return `${element}系 · 经纬`;
  if (total >= 30) return `${element}系 · 文质`;
  if (total >= 10) return `${element}系 · 乘势`;
  return `${element}系 · 孤渊`;
}

/** 给一组 features（基于元素 + 总分） */
function featuresFor(element: string, total: number): string[] {
  const traits: Record<string, string[]> = {
    金: ["金属光泽", "锋利轮廓", "秩序感"],
    木: ["向上生长", "生机勃发", "韧皮"],
    水: ["流体形", "润下无声", "深邃"],
    火: ["炽热核心", "炎上外放", "明焰"],
    土: ["厚重沉稳", "承载四方", "粗糙"],
  };
  const base = traits[element] ?? traits["土"];
  if (total >= 50) return [...base, "高阶气运"];
  if (total >= 30) return [...base, "中阶气运"];
  return base;
}

/** description（短句） */
function descriptionFor(element: string, total: number, isNameLike: boolean): string {
  if (!isNameLike) return `此条目不符合姓名规范，气运闭塞，五行无根，仅作占位记录。`;
  if (total >= 70) return `五行 ${element} 充盈，气运隆盛，当世少有。`;
  if (total >= 50) return `五行 ${element} 主宰，德音中正，可堪大任。`;
  if (total >= 30) return `五行 ${element} 得中，文质相扶，渐显锋芒。`;
  if (total >= 10) return `五行 ${element} 偏弱，待时而动，方可乘势。`;
  return `五行气机未启，${element} 不显，需潜修以待。`;
}

/**
 * 本地生成一只占位怪兽（不依赖 API）
 * @param analysis charAnalyzer 的输出
 * @param isNameLike 输入是否符合姓名规范
 */
export function generateLocalMonster(
  analysis: CharAnalysis,
  isNameLike: boolean
): MonsterDesc {
  const element = analysis.dominantElement ?? "土";
  const total = analysis.fortune?.total ?? 0;
  return {
    scpId: scpIdFor(analysis.raw),
    threat: threatFromFortune(total),
    class: classFor(element, total),
    features: featuresFor(element, total),
    description: descriptionFor(element, total, isNameLike),
    element,
    bodyType: "人形", // 屏 2 已不再使用 bodyType（直接走 figureSprite）
  };
}